import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CustomIcon } from '../../UI';
import './PhotosModal.scss';
import fileService from '../../../services/fileService';
import axios from 'axios';

const PhotosModal = ({ isOpen, onClose, photos = [], title = "Vehicle Photos" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [normalizedPhotos, setNormalizedPhotos] = useState([]);
  const thumbnailsContainerRef = useRef(null);

  const extractKeyFromUrl = (url) => {
    if (!url || typeof url !== 'string') return null;

    if (url.startsWith('blob:')) return null;

    if (url.startsWith('/api/files/') || url.startsWith('/files/')) {
      let path = url.replace(/^\/api\/files\//, '').replace(/^\/files\//, '');
      if (path.startsWith('signed-url/')) {
        path = path.replace(/^signed-url\//, '');
        try {
          return decodeURIComponent(path.split('?')[0]);
        } catch (e) {
          return path.split('?')[0];
        }
      }
      return path;
    }

    if (url.includes('.amazonaws.com/')) {
      const parts = url.split('.amazonaws.com/');
      if (parts.length > 1) {
        let key = parts[1];
        if (key.includes('?')) key = key.split('?')[0];
        if (key.includes('#')) key = key.split('#')[0];
        return key || null;
      }
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
      try {
        const parsed = new URL(url);
        let path = parsed.pathname || '';
        if (path.startsWith('/')) path = path.slice(1);

        if (path.startsWith('api/files/signed-url/') || path.startsWith('files/signed-url/')) {
          path = path.replace(/^(api\/)?files\/signed-url\//, '');
          try {
            return decodeURIComponent(path.split('?')[0]);
          } catch (e) {
            return path.split('?')[0];
          }
        }

        if (path.startsWith('api/files/')) {
          path = path.replace(/^api\/files\//, '');
        }
        if (path.startsWith('files/')) {
          path = path.replace(/^files\//, '');
        }
        return path || null;
      } catch (e) {
        return null;
      }
    }

    if (url.includes('?')) {
      return url.split('?')[0];
    }

    return url;
  };

  const isSignedUrlEndpoint = (url) => {
    if (!url || typeof url !== 'string') return false;
    return url.includes('/signed-url/') || url.includes('/signed-urls');
  };

  const normalizePhoto = (photo, index) => {
    if (!photo) return null;
    if (typeof photo === 'string') {
      const key = extractKeyFromUrl(photo);
      const isSignedUrl = photo.includes('.amazonaws.com/') && photo.includes('X-Amz-Signature');
      const isEndpoint = isSignedUrlEndpoint(photo);
      return {
        id: key ? `key-${key}` : `photo-${index}`,
        url: (isSignedUrl && !isEndpoint) ? photo : '',
        key: key || null,
        alt: `Photo ${index + 1}`,
        needsRefresh: isSignedUrl || isEndpoint
      };
    }
    const url = photo.url || photo;
    const key = photo.key || (url ? extractKeyFromUrl(url) : null);
    const isSignedUrl = url.includes('.amazonaws.com/') && url.includes('X-Amz-Signature');
    const isEndpoint = isSignedUrlEndpoint(url);
    return {
      id: photo.id || (key ? `key-${key}` : `photo-${index}`),
      url: (isSignedUrl && !isEndpoint) ? url : '',
      key: key || null,
      alt: photo.alt || `Photo ${index + 1}`,
      needsRefresh: isSignedUrl || isEndpoint || photo.needsRefresh,
      ...photo
    };
  };

  const resolveSignedUrls = async (normalized) => {
    const keysToSign = normalized
      .filter((photo) => {
        if (photo?.url?.startsWith('blob:')) return false;
        if (!photo?.key) return false;
        const url = photo.url || '';
        const isEndpoint = isSignedUrlEndpoint(url);
        const isSigned = url.includes('.amazonaws.com/') && url.includes('X-Amz-Signature');
        return !isSigned || isEndpoint;
      })
      .map((photo) => photo.key)
      .filter(Boolean);

    if (keysToSign.length === 0) {
      setNormalizedPhotos(normalized);
      return;
    }

    try {
      const urlMap = await fileService.getSignedUrls(keysToSign);
      const updated = normalized.map((photo) => {
        if (photo.key && urlMap[photo.key]) {
          return { ...photo, url: urlMap[photo.key], needsRefresh: false };
        }
        // Keep blob URLs and other URLs as is
        return photo;
      });
      setNormalizedPhotos(updated);
    } catch (error) {
      console.error('[PhotosModal] Error refreshing signed URLs:', error);
      // On error, keep current photos but mark them as needing refresh
      setNormalizedPhotos(normalized.map(photo => ({ ...photo, needsRefresh: true })));
    }
  };

  // Normalize and resolve signed URLs when photos prop changes
  useEffect(() => {
    if (photos && Array.isArray(photos)) {
      const normalized = photos.map(normalizePhoto).filter(Boolean);
      resolveSignedUrls(normalized);
    } else {
      setNormalizedPhotos([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  // Auto-refresh signed URLs every 4 minutes
  useEffect(() => {
    if (!isOpen || normalizedPhotos.length === 0) return;

    const refreshInterval = setInterval(() => {
      // Refresh signed URLs for current photos
      const photosToRefresh = normalizedPhotos.filter(p => p.key && !p.url?.startsWith('blob:'));
      if (photosToRefresh.length > 0) {
        resolveSignedUrls(normalizedPhotos);
      }
    }, 4 * 60 * 1000); // 4 minutes

    return () => clearInterval(refreshInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, normalizedPhotos.length]);

  // Handle image load error - refresh signed URL
  const handleImageError = async (photoId) => {
    const photo = normalizedPhotos.find(p => p.id === photoId);
    if (!photo?.key) return;

    try {
      const signedUrl = await fileService.getSignedUrl(photo.key, 3600);
      if (signedUrl) {
        setNormalizedPhotos(prev => 
          prev.map(p => p.id === photoId ? { ...p, url: signedUrl, needsRefresh: false } : p)
        );
      }
    } catch (error) {
      console.error('[PhotosModal] Failed to refresh signed URL on error:', error);
    }
  };

  // Reset current index when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, currentIndex, normalizedPhotos.length]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(normalizedPhotos.length - 1, prev + 1));
  };

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
  };

  const getFileName = (photo, index) => {
    const url = photo?.url || '';
    const key = photo?.key || extractKeyFromUrl(url);
    
    // Try to get filename from key
    if (key) {
      const fileName = key.split('/').pop() || '';
      if (fileName && fileName.includes('.')) {
        return fileName;
      }
    }
    
    // Fallback: extract from URL
    const cleanUrl = url.split('?')[0];
    const lastSegment = cleanUrl.split('/').pop() || '';
    if (lastSegment && lastSegment.includes('.')) {
      return lastSegment;
    }
    
    return `photo-${index + 1}.jpg`;
  };

  const getZipNameFromTitle = (t) => {
    if (!t || typeof t !== 'string') return 'vehicle-photos';
    const sanitized = t.replace(/[^a-zA-Z0-9_\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    return sanitized || 'vehicle-photos';
  };

  const downloadFile = async (photo, fileName) => {
    try {
      // Extract key from photo
      const key = photo?.key || extractKeyFromUrl(photo?.url);
      
      if (!key) {
        console.error('[PhotosModal] No key found for photo:', photo);
        return;
      }

      // Use server endpoint for download to avoid CORS issues
      const baseURL = process.env.REACT_APP_API_URL || 'https://server.ciertacorp.com';
      const encodedKey = encodeURIComponent(key);
      const downloadUrl = `${baseURL}/api/files/download/${encodedKey}${fileName ? `?filename=${encodeURIComponent(fileName)}` : ''}`;
      
      // Fetch file through server endpoint (with auth token)
      // Create axios request with auth token
      const token = localStorage.getItem('authToken');
      const response = await axios.get(downloadUrl, {
        responseType: 'blob', // Important: get response as blob
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined
        }
      });
      
      // Create blob URL and download
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'download';
      link.rel = 'noopener';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Clean up blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('[PhotosModal] Failed to download file:', error);
      // Show user-friendly error message
      alert('Failed to download file. Please try again.');
    }
  };

  // Scroll to active thumbnail
  const scrollToActiveThumbnail = () => {
    if (thumbnailsContainerRef.current) {
      const thumbnailWidth = 300 + 8; // thumbnail width + gap
      const containerWidth = thumbnailsContainerRef.current.clientWidth;
      const scrollPosition = (currentIndex * thumbnailWidth) - (containerWidth / 2) + (thumbnailWidth / 2);
      
      thumbnailsContainerRef.current.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: 'smooth'
      });
    }
  };

  // Scroll to active thumbnail when currentIndex changes
  useEffect(() => {
    if (isOpen) {
      scrollToActiveThumbnail();
    }
  }, [currentIndex, isOpen]);

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingIndex, setDownloadingIndex] = useState(-1);

  const handleDownloadZip = async () => {
    if (isDownloading) return;
    const keys = normalizedPhotos
      .map(p => p.key || extractKeyFromUrl(p?.url))
      .filter(Boolean);
    if (keys.length === 0) {
      alert('No downloadable files.');
      return;
    }
    setIsDownloading(true);
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'https://server.ciertacorp.com';
      const token = localStorage.getItem('authToken');
      const zipName = getZipNameFromTitle(title);
      const response = await axios.post(
        `${baseURL}/api/files/download-zip`,
        { keys, zipName },
        { responseType: 'blob', headers: { Authorization: token ? `Bearer ${token}` : undefined } }
      );
      const contentType = response.headers['content-type'] || '';
      if (response.status < 200 || response.status >= 300 || (contentType && !contentType.includes('application/zip') && !contentType.includes('octet-stream'))) {
        let message = 'Failed to download ZIP. Please try again.';
        if (response.data && response.data instanceof Blob && response.data.size < 4096) {
          try {
            const text = await response.data.text();
            const json = JSON.parse(text);
            if (json.error) message = json.error;
          } catch (_) {}
        }
        alert(message);
        return;
      }
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${zipName}.zip`;
      link.rel = 'noopener';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('[PhotosModal] Download ZIP failed:', error);
      const msg = error.response?.data instanceof Blob
        ? 'Failed to download ZIP. Please try again.'
        : (error.response?.data?.error || error.message || 'Failed to download ZIP. Please try again.');
      alert(msg);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadSingle = async (photo) => {
    if (isDownloading) return;
    const index = normalizedPhotos.findIndex(item => item.id === photo.id);
    const fileName = getFileName(photo, index === -1 ? 0 : index);
    setIsDownloading(true);
    setDownloadingIndex(index >= 0 ? index : 0);
    try {
      await downloadFile(photo, fileName);
    } catch (error) {
      console.error('[PhotosModal] Backend download failed, trying fetch fallback:', error);
      try {
        const res = await fetch(photo.url, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) throw new Error(res.statusText);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      } catch (fallbackError) {
        console.error('[PhotosModal] Fallback download failed:', fallbackError);
        alert('Failed to download file. Please try again.');
      }
    } finally {
      setIsDownloading(false);
      setDownloadingIndex(-1);
    }
  };

  if (!isOpen || normalizedPhotos.length === 0) return null;

  const currentPhoto = normalizedPhotos[currentIndex];

  const modalContent = (
    <div className="vehicle-photos-modal-overlay" onClick={onClose}>
      <div className="vehicle-photos-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="vehicle-photos-modal-header">
          <h2 className="vehicle-photos-modal-title">{title}</h2>
          <div className="vehicle-photos-modal-actions">
            {normalizedPhotos.length === 1 && (
              <button 
                type="button" 
                className="download-all-btn"
                onClick={() => handleDownloadSingle(normalizedPhotos[0])}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading...' : 'Download'}
              </button>
            )}
            {normalizedPhotos.length > 1 && (
              <button 
                type="button" 
                className="download-all-btn"
                onClick={handleDownloadZip}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading...' : 'Download as ZIP'}
              </button>
            )}
            <button 
              type="button" 
              className="close-btn"
              onClick={onClose}
            >
              <CustomIcon name="close" size={32} />
            </button>
          </div>
        </div>

        {/* Main Image Display */}
        <div className="main-image-container">
          <div className="main-image-wrapper">
            <img 
              src={currentPhoto.url} 
              alt={currentPhoto.alt || `Vehicle photo ${currentIndex + 1}`}
              className="main-image"
              onError={() => handleImageError(currentPhoto.id)}
              loading="lazy"
            />
            {normalizedPhotos.length >= 1 && (
              <button 
                type="button"
                className="download-single-btn"
                onClick={() => handleDownloadSingle(currentPhoto)}
                title="Download this image"
                disabled={isDownloading}
              >
                {isDownloading && downloadingIndex === currentIndex ? (
                  <span className="download-spinner">⏳</span>
                ) : (
                  <CustomIcon name="download" size={32} type='svg'/>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Thumbnail Gallery */}
        {normalizedPhotos.length > 1 && (
          <div className="thumbnail-gallery">
            <button 
              type="button"
              className="vehicle-photos-modal-nav-btn vehicle-photos-modal-nav-btn-left"
                onClick={handlePrevious}
                disabled={currentIndex === 0}
              >
              <CustomIcon name="left" size={20} />
            </button>

            <div className="thumbnails-container" ref={thumbnailsContainerRef}>
              <div className="thumbnails-track">
                {normalizedPhotos.map((photo, index) => (
                  <div
                    key={photo.id || index}
                    className={`thumbnail ${index === currentIndex ? 'active' : ''}`}
                    onClick={() => handleThumbnailClick(index)}
                  >
                    <img 
                      src={photo.url} 
                      alt={photo.alt || `Thumbnail ${index + 1}`}
                      className="thumbnail-image"
                      onError={() => handleImageError(photo.id)}
                      loading="lazy"
                    />
                    {normalizedPhotos.length >= 1 && (
                      <button 
                        type="button"
                        className="thumbnail-download-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadSingle(photo);
                        }}
                        title="Download this image"
                        disabled={isDownloading}
                      >
                        {isDownloading && downloadingIndex === index ? (
                          <span className="download-spinner-small">⏳</span>
                        ) : (
                          <CustomIcon name="download" size={19} type='svg' />
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <button 
              type="button"
              className="vehicle-photos-modal-nav-btn vehicle-photos-modal-nav-btn-right"
              onClick={handleNext}
              disabled={currentIndex === normalizedPhotos.length - 1}
            >
              <CustomIcon name="right" size={20} />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default PhotosModal;
